import React, { useEffect } from "react";
import Loadable from "react-loadable";
import { useLocation } from "react-router";

import {
  focusModeWhitelistedRoutes,
  pageViewBlacklistedRoutes,
} from "@config/app.routing";
import { TITLE_TEMPLATE_BATMAID } from "@config/consts";

import { DefaultLayout } from "@ui/Layouts";

import { NavBar } from "@features/NavBar";

import { isHiddenEnv } from "@services/IsHiddenEnv";
import { isHeadlessMode } from "@services/HeadlessMode";

import { useAuth } from "@hooks/useAuth";
import { useTelemetry } from "@hooks/useTelemetry";
import { useBreakpoint } from "@hooks/useBreakpoint";
import { useTranslate } from "@hooks/useTranslate";
import { useFeatureToggle } from "@hooks/useFeatureToggle";
import { useLocalisation } from "@hooks/useLocalisation";
import { useMatchPath } from "@hooks/useMatchPath";

import {
  FooterContainer,
  LoadingIndicator as Loading,
  BottomNavigation,
} from "@app/containers";

const LoadableUserNotificationBar = Loadable({
  loader: () => import("@app/containers/UserNotificationBar"),
  render(loaded: any): JSX.Element {
    const Component = loaded.UserNotificationBar;
    return <Component />;
  },
  loading: () => null,
});

interface Props {
  children: React.ReactNode;
}

const DefaultLayoutApp = (props: Props): React.ReactElement => {
  const { pathname } = useLocation();
  const translate = useTranslate();
  const { isUserClient } = useAuth();
  const { desktop, desktopLarge } = useBreakpoint("v1");
  const { pageView } = useTelemetry();
  const { country, language } = useLocalisation();
  const matchPath = useMatchPath();

  /**
   * Use this to prevent firing automatic pageView event on selected routes
   */
  const isBlacklistedRoute = pageViewBlacklistedRoutes.some(matchPath);
  const isFocusModeRoute = focusModeWhitelistedRoutes.some(matchPath);

  const isHeaden = isHiddenEnv();
  const isHeadless = isHeadlessMode();
  const isTeaserVersion = useFeatureToggle("teaser_version_enabled_hidden");
  const isDesktop = desktop || desktopLarge;

  useEffect(() => {
    const [_, ...rest] = pathname.split("/");

    !isBlacklistedRoute &&
      pageView({
        pageId: rest.join("-"),
      });
  }, [pathname]);

  return (
    <DefaultLayout
      country={country}
      title={translate("batmaid_pwa_generic.meta_title")}
      titleTemplate={TITLE_TEMPLATE_BATMAID}
      header={
        <React.Fragment>
          {!isHeadless && <NavBar language={language} />}
        </React.Fragment>
      }
      loading={<Loading />}
      footer={
        isDesktop ? (
          <FooterContainer isTeaserVersion={isHeaden && isTeaserVersion} />
        ) : (
          <React.Fragment />
        )
      }
      bottomNavigation={!isDesktop ? <BottomNavigation /> : undefined}
      somethingWentWrongText={translate(
        "batmaid_pwa_generic.something_went_wrong",
      )}
      appCrashExplanationText={translate(
        "batmaid_pwa_generic.app_crash_explanation",
      )}
    >
      {!isFocusModeRoute && isUserClient && <LoadableUserNotificationBar />}

      {props.children}
    </DefaultLayout>
  );
};

DefaultLayoutApp.displayName = "DefaultLayoutApp";

export { DefaultLayoutApp };
